import { Check } from '@phosphor-icons/react';
import type { ImportWizardStep } from '@discovr/features';
import { cn } from '../../../lib/utils';

type StepperState = 'done' | 'current' | 'upcoming';

// One track for every import target. Importing still shows Sheet review as
// current, since the grid stays on screen while the import runs, and done
// sits past the last step. An outreach sheet whose handle column is
// recognised passes Column mapping without stopping there.
const STEP_LABELS = ['Upload', 'Column mapping', 'Sheet review'] as const;
const ACTIVE_INDEX: Record<ImportWizardStep, number> = {
  upload: 0,
  mapping: 1,
  review: 2,
  importing: 2,
  done: STEP_LABELS.length,
};

const CHECK_SIZE = 12;

const LABEL_CLASS: Record<StepperState, string> = {
  done: 'text-foreground',
  current: 'text-body-sm-medium text-foreground',
  upcoming: 'text-muted-foreground',
};

const DOT_CLASS: Record<StepperState, string> = {
  done: 'border-success bg-success text-success-foreground',
  current: 'border-primary bg-primary',
  upcoming: 'border-border',
};

function stepperState(index: number, activeIndex: number): StepperState {
  if (index < activeIndex) return 'done';
  if (index === activeIndex) return 'current';
  return 'upcoming';
}

function StepDot({ state }: { state: StepperState }) {
  return (
    <span aria-hidden="true" className={cn('flex size-[22px] shrink-0 items-center justify-center rounded-full border', DOT_CLASS[state])}>
      {state === 'done' && <Check size={CHECK_SIZE} weight="bold" data-testid="step-check" />}
      {state === 'current' && <span className="size-2 rounded-full bg-primary-foreground" />}
    </span>
  );
}

function StepLine({ done }: { done: boolean }) {
  return <span aria-hidden="true" className={cn('h-px w-8 shrink-0', done ? 'bg-success' : 'bg-border')} />;
}

export function ImportStepper({ step }: { step: ImportWizardStep }) {
  const activeIndex = ACTIVE_INDEX[step];
  return (
    <ol aria-label="Import progress" className="flex flex-wrap items-center gap-3 text-body-sm-regular">
      {STEP_LABELS.map((label, index) => {
        const state = stepperState(index, activeIndex);
        // The connector lives inside the item: an <ol> may only hold <li>s.
        return (
          <li
            key={label}
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
            className={cn('flex items-center gap-2', LABEL_CLASS[state])}
          >
            {index > 0 && <StepLine done={index <= activeIndex} />}
            <StepDot state={state} />
            {label}
          </li>
        );
      })}
    </ol>
  );
}
